import React, {useState} from "react";
import axios from "axios";
import firebase from "firebase/app";
import "firebase/firestore";
import {Modal, Form} from "react-bootstrap";
import {FaCheck, FaExclamationCircle, FaFolderOpen} from "react-icons/fa";
import {useDispatch, useSelector} from "react-redux";
import {database} from "../../../../firebase";
import {USER_ROLES} from "../../../../constants/routes";
import {updateAccounts} from "../../../../redux/account/account.actions";
import {
  selectAccounts,
  selectAccountId,
} from "../../../../redux/account/account.selectors";
import {selectCurrentUser} from "../../../../redux/user/user.selectors";
import {renderDate} from "../../../../utils/RenderDate";
import ReelerButton from "../../../commons/ReelerButton";
import ReelerTooltip from "../../../commons/ReelerTooltip/ReelerTooltip";
import SpinnerComponent from "../../../commons/SpinnerComponent";
import "../../../App.css";

const ScaleflexIntegration = () => {
  const dispatch = useDispatch();
  const account = useSelector(selectAccounts);
  const accountId = useSelector(selectAccountId);
  const currentUser = useSelector(selectCurrentUser);

  const existingIntegration = account?.integrations?.scaleflex;

  const [show, setShow] = useState(false);
  const [activeAction, setActiveAction] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [customerToken, setCustomerToken] = useState(
    existingIntegration?.customerToken || "",
  );
  const [accessKey, setAccessKey] = useState(
    existingIntegration?.accessKey || "",
  );
  const [folder, setFolder] = useState(existingIntegration?.folder || "");

  const integrationValueStyle = {
    minWidth: 0,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  };

  const normalizeFolder = (folderValue) => {
    const trimmedFolder = folderValue.trim();

    if (!trimmedFolder) return "";
    return trimmedFolder.startsWith("/") ? trimmedFolder : `/${trimmedFolder}`;
  };

  const maskAccessKey = (key) => {
    if (!key) return "";
    if (key.length <= 4) return "•".repeat(key.length);
    return `${"•".repeat(Math.max(key.length - 4, 4))}${key.slice(-4)}`;
  };

  const clearMessages = () => {
    setError(null);
    setSuccessMessage(null);
  };

  const openModal = () => {
    setCustomerToken(existingIntegration?.customerToken || "");
    setAccessKey(existingIntegration?.accessKey || "");
    setFolder(existingIntegration?.folder || "");
    clearMessages();
    setShow(true);
  };

  const closeModal = () => {
    if (!activeAction) {
      setShow(false);
      clearMessages();
    }
  };

  const validateFields = () => {
    if (!customerToken.trim() || !accessKey.trim() || !folder.trim()) {
      setError("Please fill in customer token, access key and folder.");
      return false;
    }

    return true;
  };

  const buildScaleflexData = () => ({
    customerToken: customerToken.trim(),
    accessKey: accessKey.trim(),
    folder: normalizeFolder(folder),
    createdAt: existingIntegration?.createdAt || new Date(),
    integratedBy: existingIntegration?.integratedBy || currentUser.id,
    updatedAt: new Date(),
    updatedBy: currentUser.id,
  });

  const testIntegration = async () => {
    if (!validateFields()) return;

    setActiveAction("test");
    clearMessages();

    try {
      await axios.get(
        `https://api.filerobot.com/${customerToken.trim()}/v5/files`,
        {
          params: {
            folder: normalizeFolder(folder),
          },
          headers: {
            "X-Filerobot-Key": accessKey.trim(),
            "Content-Type": "application/json",
          },
        },
      );

      setSuccessMessage("Scaleflex connection successful.");
    } catch (err) {
      console.log(err);
      setError(
        err?.response?.data?.message ||
          "Scaleflex test failed. Please verify customer token, access key and folder.",
      );
    }

    setActiveAction(null);
  };

  const saveIntegration = async () => {
    if (!validateFields()) return;

    setActiveAction("save");
    clearMessages();

    const data = buildScaleflexData();

    try {
      await database.accounts.doc(accountId).update({
        "integrations.scaleflex": data,
      });

      dispatch(
        updateAccounts({
          integrations: {
            ...account.integrations,
            scaleflex: data,
          },
        }),
      );

      setShow(false);
      setSuccessMessage("Scaleflex integration saved.");
    } catch (err) {
      console.log(err);
      setError(
        "Reeler could not save the Scaleflex integration. Please contact Reeler support.",
      );
    }

    setActiveAction(null);
  };

  const removeIntegration = async () => {
    if (!existingIntegration) return;

    const confirmed = window.confirm(
      "Are you sure you want to remove the Scaleflex integration?",
    );

    if (!confirmed) return;

    setActiveAction("remove");
    clearMessages();

    try {
      await database.accounts.doc(accountId).update({
        "integrations.scaleflex": firebase.firestore.FieldValue.delete(),
      });

      const remainingIntegrations = {...(account.integrations || {})};
      delete remainingIntegrations.scaleflex;

      dispatch(
        updateAccounts({
          integrations: remainingIntegrations,
        }),
      );

      setCustomerToken("");
      setAccessKey("");
      setFolder("");
      setShow(false);
      setSuccessMessage("Scaleflex integration removed.");
    } catch (err) {
      console.log(err);
      setError(
        "Reeler could not remove the Scaleflex integration. Please contact Reeler support.",
      );
    }

    setActiveAction(null);
  };

  const ManageButton = () => (
    <ReelerTooltip
      text={
        currentUser?.role !== USER_ROLES.owner
          ? "Only the owner can manage Scaleflex"
          : ""
      }
    >
      <ReelerButton
        loading={activeAction === "save"}
        dispatch={openModal}
        text="Manage"
        styleClass="btn-main"
        disabled={currentUser?.role !== USER_ROLES.owner}
      />
    </ReelerTooltip>
  );

  return account ? (
    <>
      <div
        className="ms-3 p-3"
        style={{
          border: "1px solid lightgrey",
          borderRadius: "5px",
          width: "300px",
        }}
      >
        <h5>
          <FaFolderOpen className="icon-color me-3" />
          Scaleflex
        </h5>
        <p>
          Configure your Scaleflex credentials to let Reeler store and manage
          assets in your selected Scaleflex folder.
        </p>

        {successMessage ? (
          <div className="d-flex flex-row align-items-center mb-3">
            <FaCheck style={{color: "var(--reeler)"}} className="me-2" />
            <span>{successMessage}</span>
          </div>
        ) : null}

        {error ? (
          <div className="d-flex flex-row align-items-center mb-3">
            <FaExclamationCircle
              style={{color: "var(--reeler-danger)"}}
              className="me-2"
            />
            <span>{error}</span>
          </div>
        ) : null}

        {existingIntegration?.customerToken ? (
          <div className="d-flex flex-column">
            <div className="d-flex flex-row align-items-center mb-1">
              <FaCheck style={{color: "var(--reeler)"}} className="me-2" />
              <span>{`Connected (${renderDate(
                existingIntegration?.updatedAt ||
                  existingIntegration?.createdAt,
                "YYYY-MM-DD",
              )})`}</span>
            </div>
            <div className="d-flex flex-row align-items-start mb-1">
              <span className="me-2 flex-shrink-0">Folder:</span>
              <span style={integrationValueStyle}>
                {existingIntegration?.folder}
              </span>
            </div>
            <div className="d-flex flex-row align-items-start mb-3">
              <span className="me-2 flex-shrink-0">Access key:</span>
              <span style={integrationValueStyle}>
                {maskAccessKey(existingIntegration?.accessKey)}
              </span>
            </div>
            <ManageButton />
          </div>
        ) : (
          <ManageButton />
        )}
      </div>

      <Modal centered show={show} onHide={closeModal}>
        <Modal.Header closeButton>
          <Modal.Title>Manage Scaleflex</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="scaleflexCustomerToken">
            <Form.Label>Customer token</Form.Label>
            <Form.Control
              type="text"
              value={customerToken}
              onChange={(event) => setCustomerToken(event.target.value)}
              placeholder="Enter customer token"
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="scaleflexAccessKey">
            <Form.Label>Access key</Form.Label>
            <Form.Control
              type="password"
              value={accessKey}
              onChange={(event) => setAccessKey(event.target.value)}
              placeholder="Enter access key"
            />
          </Form.Group>
          <Form.Group controlId="scaleflexFolder">
            <Form.Label>Folder</Form.Label>
            <Form.Control
              type="text"
              value={folder}
              onChange={(event) => setFolder(event.target.value)}
              placeholder="Enter folder"
            />
          </Form.Group>

          {successMessage ? (
            <div className="d-flex flex-row align-items-center mt-3">
              <FaCheck style={{color: "var(--reeler)"}} className="me-2" />
              <span>{successMessage}</span>
            </div>
          ) : null}

          {error ? (
            <div className="d-flex flex-row align-items-center mt-3">
              <FaExclamationCircle
                style={{color: "var(--reeler-danger)"}}
                className="me-2"
              />
              <span>{error}</span>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <ReelerButton
            text="Cancel"
            dispatch={closeModal}
            styleClass="btn-secondary"
            disabled={!!activeAction}
          />
          <ReelerButton
            text="Test"
            dispatch={testIntegration}
            styleClass="btn-secondary"
            loading={activeAction === "test"}
            disabled={activeAction === "save" || activeAction === "remove"}
          />
          <ReelerButton
            text="Remove"
            dispatch={removeIntegration}
            styleClass="btn-danger"
            loading={activeAction === "remove"}
            disabled={
              !existingIntegration ||
              activeAction === "save" ||
              activeAction === "test"
            }
          />
          <ReelerButton
            text="Save"
            dispatch={saveIntegration}
            styleClass="btn-main"
            loading={activeAction === "save"}
            disabled={activeAction === "test" || activeAction === "remove"}
          />
        </Modal.Footer>
      </Modal>
    </>
  ) : (
    <SpinnerComponent />
  );
};

export default ScaleflexIntegration;
